import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Trash2, Edit3, Copy, RotateCcw, Code, Download, Upload } from 'lucide-react';
import { useDarkMode } from 'context/DarkModeProvider';
import "../../assets/css/CreateYourOwn/StackBuilder.css";
import { useLanguage } from 'context/LanguageProvider';
import CodeHighlighter from 'components/CodeHighlighter'; // ή όπου το βάλεις

const StackBuilder = () => {
    const [stack, setStack] = useState([]);
    const [draggedItem, setDraggedItem] = useState(null);
    const [draggedFromStack, setDraggedFromStack] = useState(false);
    const [nextId, setNextId] = useState(1);
    const [editingId, setEditingId] = useState(null);
    const [newItemValue, setNewItemValue] = useState('');
    const [selectedOperation, setSelectedOperation] = useState('push');
    const [animatingItem, setAnimatingItem] = useState(null);
    const [showCode, setShowCode] = useState(false);
    const [codeLanguage, setCodeLanguage] = useState('javascript');
    const [dragOverZone, setDragOverZone] = useState(null);
    const [hoverItem, setHoverItem] = useState(null);
    const [showInfo, setShowInfo] = useState(false);
    const [copied, setCopied] = useState(false);
    const [actionLog, setActionLog] = useState([]);
    const { darkMode } = useDarkMode();

    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    const stackRef = useRef(null);
    const dropZoneRef = useRef(null);
    const garbageRef = useRef(null);

    const colors = ['#8B5CF6'];

    // Animation helper
    const animateOperation = (operation, item = null) => {
        setAnimatingItem({ operation, item });
        setTimeout(() => setAnimatingItem(null), 600);
    };

    // Stack operations
    const push = (value, color = colors[Math.floor(Math.random() * colors.length)]) => {
        const newItem = {
            id: nextId,
            value: value || `Item ${nextId}`,
            color
        };
        setStack(prev => [newItem, ...prev]);
        setNextId(prev => prev + 1);
        setActionLog(prev => [...prev, { type: 'push', value: newItem.value }]);
    };

    const pop = () => {
        if (stack.length === 0) return null;
        const item = stack[0];
        setStack(prev => prev.slice(1));
        setActionLog(prev => [...prev, { type: 'pop', value: item.value }]);
        return item;
    };

    const peek = () => {
        if (stack.length === 0) return null;
        const topItem = stack[0];
        setHoverItem(topItem.id);
        setTimeout(() => setHoverItem(null), 1000);
        setActionLog(prev => [...prev, { type: 'peek', value: topItem.value }]);
        return topItem;
    };

    const handleDrop = (e, dropZone) => {
        e.preventDefault();
        setDragOverZone(null);

        if (!draggedItem) return;

        if (dropZone === 'stack') {
            if (!draggedFromStack) {
                const newItem = {
                    id: nextId,
                    value: draggedItem.value,
                    color: draggedItem.color
                };
                setStack(prev => [newItem, ...prev]);
                setNextId(prev => prev + 1);
                setActionLog(prev => [...prev, { type: 'push', value: draggedItem.value }]);
            } else {
                setStack(prev => {
                    const filtered = prev.filter(item => item.id !== draggedItem.id);
                    return [draggedItem, ...filtered];
                });
            }
        } else if (dropZone === 'garbage' && draggedFromStack) {
            setStack(prev => prev.filter(item => item.id !== draggedItem.id));
            setActionLog(prev => [...prev, { type: 'pop', value: draggedItem.value }]);
            animateOperation('delete', draggedItem);
        }

        setDraggedItem(null);
        setDraggedFromStack(false);
    };

    const clear = () => {
        setStack([]);
        setActionLog(prev => [...prev, { type: 'clear' }]);
    };

    // Drag and Drop handlers
    const handleDragStart = (e, item, fromStack = false) => {
        setDraggedItem(item);
        setDraggedFromStack(fromStack);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', '');

        const dragImage = document.createElement('div');
        dragImage.innerHTML = item.value;
        dragImage.style.cssText = `
            position: absolute;
            top: -1000px;
            background: ${item.color};
            padding: 10px;
            border-radius: 8px;
            border: 2px solid #333;
            font-weight: bold;
            box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        `;
        document.body.appendChild(dragImage);
        e.dataTransfer.setDragImage(dragImage, 50, 25);
        setTimeout(() => document.body.removeChild(dragImage), 0);
    };

    const handleDragOver = (e, zone = null) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverZone(zone);
    };

    const handleDragLeave = () => {
        setDragOverZone(null);
    };

    const createDraggableItem = () => {
        const value = newItemValue.trim() || `Item ${nextId}`;
        const color = colors[Math.floor(Math.random() * colors.length)];
        const newItem = {
            id: Date.now(),
            value,
            color
        };
        setNewItemValue('');
        return newItem;
    };

    const editItem = (id, newValue) => {
        setStack(prev => prev.map(item =>
            item.id === id ? { ...item, value: newValue } : item
        ));
        setEditingId(null);
    };

    const generateCode = () => {
        const renderActions = (lang) => {
            return actionLog.map(action => {
                if (action.type === 'push') {
                    if (lang === 'javascript') return `myStack.push("${action.value}");`;
                    if (lang === 'python') return `my_stack.push("${action.value}")`;
                    if (lang === 'java') return `        push("${action.value}");`;
                    if (lang === 'cpp') return `    push("${action.value}");`;
                }
                if (action.type === 'pop') {
                    if (lang === 'javascript') return `myStack.pop(); // removed "${action.value}"`;
                    if (lang === 'python') return `my_stack.pop()  # removed "${action.value}"`;
                    if (lang === 'java') return `        pop(); // removed "${action.value}"`;
                    if (lang === 'cpp') return `    pop(); // removed "${action.value}"`;
                }
                if (action.type === 'peek') {
                    if (lang === 'javascript') return `myStack.peek(); // peeked "${action.value}"`;
                    if (lang === 'python') return `my_stack.peek()  # peeked "${action.value}"`;
                    if (lang === 'java') return `        peek(); // peeked "${action.value}"`;
                    if (lang === 'cpp') return `    peek(); // peeked "${action.value}"`;
                }
                if (action.type === 'clear') {
                    if (lang === 'javascript') return `// Stack was cleared at this point`;
                    if (lang === 'python') return `# Stack was cleared at this point`;
                    if (lang === 'java') return `        // Stack was cleared at this point`;
                    if (lang === 'cpp') return `    // Stack was cleared at this point`;
                }
                return '';
            }).join('\n');
        };

        const codes = {
            javascript: `// Stack Implementation in JavaScript
class Stack {
    constructor() {
        this.items = [];
    }
    
    push(element) {
        this.items.push(element);
    }
    
    pop() {
        if (this.isEmpty()) return null;
        return this.items.pop();
    }
    
    peek() {
        if (this.isEmpty()) return null;
        return this.items[this.items.length - 1];
    }
    
    isEmpty() {
        return this.items.length === 0;
    }
    
    size() {
        return this.items.length;
    }
}

// Your actions:
const myStack = new Stack();
${renderActions('javascript')}`,

            python: `# Stack Implementation in Python
class Stack:
    def __init__(self):
        self.items = []
    
    def push(self, element):
        self.items.append(element)
    
    def pop(self):
        if self.is_empty():
            return None
        return self.items.pop()
    
    def peek(self):
        if self.is_empty():
            return None
        return self.items[-1]
    
    def is_empty(self):
        return len(self.items) == 0
    
    def size(self):
        return len(self.items)

# Your actions:
my_stack = Stack()
${renderActions('python')}`,

            java: `// Stack Implementation in Java
import java.util.ArrayList;

public class Main {
    static ArrayList<String> stack = new ArrayList<>();

    public static void push(String element) {
        stack.add(element);
    }

    public static String pop() {
        if (stack.isEmpty()) return null;
        return stack.remove(stack.size() - 1);
    }

    public static String peek() {
        if (stack.isEmpty()) return null;
        return stack.get(stack.size() - 1);
    }

    public static boolean isEmpty() {
        return stack.isEmpty();
    }

    public static int size() {
        return stack.size();
    }

    public static void main(String[] args) {
        // Your actions:
${renderActions('java')}
    }
}`,

            cpp: `// Stack Implementation in C++
#include <iostream>
#include <vector>
#include <string>
using namespace std;

vector<string> stack;

void push(const string& element) {
    stack.push_back(element);
}

string pop() {
    if (stack.empty()) return "";
    string top = stack.back();
    stack.pop_back();
    return top;
}

string peek() {
    if (stack.empty()) return "";
    return stack.back();
}

bool isEmpty() {
    return stack.empty();
}

int size() {
    return (int)stack.size();
}

int main() {
    // Your actions:
${renderActions('cpp')}
    return 0;
}`
        };

        return codes[codeLanguage];
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.ctrlKey || e.metaKey) {
                switch (e.key) {
                    case 'Enter':
                        e.preventDefault();
                        if (newItemValue.trim()) {
                            const item = createDraggableItem();
                            push(item.value, item.color);
                        }
                        break;
                    case 'Backspace':
                        e.preventDefault();
                        pop();
                        break;
                    case 'r':
                        e.preventDefault();
                        clear();
                        break;
                }
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [newItemValue]);

    const containerStyle = {
        display: 'flex',
        gap: '30px',
        padding: '30px',
        background: darkMode ? '#0f172a' : 'var(--lightModeBack)',
        minHeight: '100vh',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    };

    const visualizationContainerStyle = {
        flex: 1,
        background: darkMode ? '#1E293B' : 'var(--customBuildBack)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        padding: '30px',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderColor: darkMode ? 'rgba(122, 142, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)',
    };

    const codeDisplayStyle = {
        background: 'linear-gradient(145deg, #0f172a, #1e293b)',
        color: '#22d3ee',
        padding: '24px',
        borderRadius: '16px',
        fontFamily: "'Fira Code', 'Monaco', 'Cascadia Code', monospace",
        fontSize: '0.85rem',
        lineHeight: '1.6',
        overflow: 'auto',
        maxHeight: '500px',
        boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.3)',
        border: '1px solid #334155'
    };

    const titleStyle = {
        fontSize: '2.5rem',
        fontWeight: '800',
        background: "rgb(122, 142, 255)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: 'text',
        textAlign: 'center',
        marginBottom: '30px',
        letterSpacing: '-0.02em'
    };

    const inputStyle = {
        width: "62%",
        padding: '16px 20px',
        border: '2px solid #e5e7eb',
        borderRadius: '16px',
        fontSize: '1rem',
        background: 'white',
        transition: 'all 0.2s ease',
        fontFamily: 'inherit'
    };

    const buttonStyle = {
        padding: '16px 24px',
        borderRadius: '16px',
        border: 'none',
        fontSize: '1rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontFamily: 'inherit',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    };

    return (
        <div className="stack-builder-container" style={containerStyle}>
            <div className="visualizationContainerStyle" style={visualizationContainerStyle}>
                <h1 style={titleStyle}>
                    {selLanguage ? "Δημιουργία Στοίβας" : "Stack Builder"}
                </h1>

                {/* Controls */}
                <div className="sb-controls">
                    <div className="sb-control-group">
                        <label style={{
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            color: darkMode ? 'white' : 'black',
                            marginBottom: '8px',
                            letterSpacing: '0.025em',
                            display: 'block',
                        }}>
                            {selLanguage ? "Δημιουργία νέου στοιχείου" : "Create New Item"}
                        </label>
                        <div className="sb-input-container">
                            <input
                                type="text"
                                value={newItemValue}
                                onChange={(e) => setNewItemValue(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        const item = createDraggableItem();
                                        push(item.value, item.color);
                                    }
                                }}
                                placeholder={selLanguage ? "Πρόσθεσε την τιμή..." : "Enter Value..."}
                                style={inputStyle}
                                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                className="custom-input"
                            />
                            <div style={{ position: 'relative' }}>
                                <button
                                    className="commoninfoButton"
                                    style={{
                                        ...buttonStyle,
                                        background: darkMode ? "#1e293b" : "var(--customBuildButton)",
                                        fontFamily: "Poppins, sans-serif",
                                        color: "#7a8effff",
                                        border: "2px solid #7a8effff",
                                        width: '56px',
                                        height: '56px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    onMouseEnter={() => setShowInfo(true)}
                                    onMouseLeave={() => setShowInfo(false)}
                                >
                                    💡
                                </button>
                                {showInfo && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        marginTop: '10px',
                                        padding: '20px',
                                        background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.98), rgba(51, 65, 85, 0.98))',
                                        backdropFilter: 'blur(20px)',
                                        borderRadius: '16px',
                                        border: '1px solid rgba(122, 142, 255, 0.3)',
                                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                                        zIndex: 1000,
                                        minWidth: '280px',
                                    }}>
                                        <div style={{
                                            position: 'absolute',
                                            top: '-8px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: '0',
                                            height: '0',
                                            borderLeft: '8px solid transparent',
                                            borderRight: '8px solid transparent',
                                            borderBottom: '8px solid rgba(30, 41, 59, 0.98)'
                                        }} />

                                        <h3 style={{
                                            fontSize: '1.1rem',
                                            fontWeight: '600',
                                            color: '#ffffff',
                                            marginBottom: '15px',
                                            textAlign: 'center'
                                        }}>
                                            {selLanguage ? "Οδηγίες" : "Instructions"}
                                        </h3>
                                        <div style={{
                                            fontSize: '0.85rem',
                                            color: '#cbd5e1',
                                            lineHeight: '1.6'
                                        }}>
                                            <div><strong>Enter:</strong>{selLanguage ? " Προσθήκη νέου στοιχείου" : " Push new item"}</div>
                                            <div><strong>Ctrl+Backspace:</strong>{selLanguage ? " Αφαίρεση στοιχείου" : " Pop item"}</div>
                                            <div><strong>Ctrl+R:</strong>{selLanguage ? " Καθαρισμός στοίβας" : " Clear stack"}</div>
                                            <div><strong>Double-click:</strong>{selLanguage ? " Επεξεργασία στοιχείου" : " Edit item"}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <style>
                            {`
                    .custom-input::placeholder {
                        color: #363a42ff;
                        opacity: 1;
                    }
                    `}
                        </style>
                    </div>
                </div>

                {/* Operation Buttons */}
                <div className="sb-op-buttons">
                    <button
                        onClick={() => {
                            const item = createDraggableItem();
                            push(item.value, item.color);
                        }}
                        className="commonCreateButton"
                        style={{
                            ...buttonStyle,
                            background: darkMode ? "#1e293b" : "var(--customBuildButton)",
                            fontFamily: "Poppins, sans-serif",
                            color: "#7a8effff",
                            border: "2px solid #7a8effff",
                        }}
                        onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                        onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                        {selLanguage ? "Πρόσθηκη στοιχείου" : "Push Item"}
                    </button>
                    <button
                        onClick={pop}
                        disabled={stack.length === 0}
                        className="commonCreateButton"
                        style={{
                            ...buttonStyle,
                            background: darkMode ? "#1e293b" : "var(--customBuildButton)",
                            fontFamily: "Poppins, sans-serif",
                            color: "#7a8effff",
                            border: "2px solid #7a8effff",
                            opacity: stack.length === 0 ? 0.5 : 1,
                        }}
                        onMouseOver={(e) => !e.target.disabled && (e.target.style.transform = 'translateY(-2px)')}
                        onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                        {selLanguage ? "Αφαίρεση στοιχείου" : "Pop Item"}
                    </button>
                    <button
                        onClick={peek}
                        disabled={stack.length === 0}
                        className="commonCreateButton"
                        style={{
                            ...buttonStyle,
                            background: darkMode ? "#1e293b" : "var(--customBuildButton)",
                            fontFamily: "Poppins, sans-serif",
                            color: "#7a8effff",
                            border: "2px solid #7a8effff",
                            opacity: stack.length === 0 ? 0.5 : 1,
                        }}
                        onMouseOver={(e) => !e.target.disabled && (e.target.style.transform = 'translateY(-2px)')}
                        onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                        {selLanguage ? "Προβολή Κορυφής" : "Peek Top"}
                    </button>
                    <button
                        onClick={clear}
                        disabled={stack.length === 0}
                        className="commonCreateButton"
                        style={{
                            ...buttonStyle,
                            background: darkMode ? "#1e293b" : "var(--customBuildButton)",
                            fontFamily: "Poppins, sans-serif",
                            color: "#7a8effff",
                            border: "2px solid #7a8effff",
                            opacity: stack.length === 0 ? 0.5 : 1,
                        }}
                        onMouseOver={(e) => !e.target.disabled && (e.target.style.transform = 'translateY(-2px)')}
                        onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                        {selLanguage ? "Καθαρισμός" : "Restart"}
                    </button>
                </div>

                {/* Stack Visualization */}
                <div className="sb-visualization">
                    <div
                        ref={stackRef}
                        onDragOver={(e) => handleDragOver(e, 'stack')}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, 'stack')}
                        className="sb-stack-container"
                        style={{
                            border: dragOverZone === 'stack'
                                ? '3px dashed #4facfe'
                                : darkMode ? '2px solid #e5e7eb' : '2px solid #868a92ff',
                        }}
                    >
                        <div style={{
                            position: 'absolute',
                            top: '-15px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            color: 'white',
                            padding: '8px 20px',
                            borderRadius: '20px',
                            fontWeight: '700',
                            fontSize: '1rem',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            border: '2px solid rgba(255, 255, 255, 0.2)'
                        }}>
                            STACK
                        </div>

                        <div style={{
                            position: 'absolute',
                            bottom: '15px',
                            left: '15px',
                            right: '15px',
                            height: '8px',
                            background: "linear-gradient(135deg, rgb(245, 247, 250) 0%, rgb(195, 207, 226) 100%)",
                            borderRadius: '4px',
                            boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)'
                        }} />

                        <div className="sb-stack-items">
                            {stack.length === 0 ? (
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    textAlign: 'center',
                                    color: '#9ca3af',
                                    fontSize: '1.1rem',
                                    fontWeight: '500'
                                }}>
                                    <div style={{ fontSize: '3rem', marginBottom: '10px' }}>📦</div>
                                    <div>{selLanguage ? "Άδεια στοίβα" : "Empty Stack"}</div>
                                    <div style={{ fontSize: '0.9rem', marginTop: '8px', opacity: 0.7 }}>
                                        {selLanguage ? "Σύρε στοιχεία εδώ ή χρησιμοποίησε τις λειτουργίες" : "Drag items here or use controls"}
                                    </div>
                                </div>
                            ) : (
                                stack.map((item, index) => (
                                    <div
                                        key={item.id}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, item, true)}
                                        onDoubleClick={() => setEditingId(item.id)}
                                        style={{
                                            padding: '16px 20px',
                                            background: hoverItem === item.id
                                                ? `linear-gradient(135deg, ${item.color}, #ffffff)`
                                                : `linear-gradient(135deg, ${item.color}, ${item.color}dd)`,
                                            borderRadius: '12px',
                                            border: index === 0
                                                ? '3px solid #667eea'
                                                : '2px solid rgba(255, 255, 255, 0.3)',
                                            cursor: 'grab',
                                            textAlign: 'center',
                                            fontWeight: '600',
                                            fontSize: '1rem',
                                            color: 'white',
                                            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
                                            boxShadow: index === 0
                                                ? '0 8px 25px rgba(102, 126, 234, 0.4)'
                                                : '0 4px 12px rgba(0, 0, 0, 0.15)',
                                            transform: animatingItem?.operation === 'push' && animatingItem?.item?.id === item.id
                                                ? 'scale(1.05) translateY(-8px)'
                                                : animatingItem?.operation === 'pop' && index === 0
                                                    ? 'scale(0.95) translateY(-15px) rotate(3deg)'
                                                    : hoverItem === item.id
                                                        ? 'scale(1.02) translateY(-3px)'
                                                        : 'scale(1)',
                                            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                            zIndex: index === stack.length - 1 ? 10 : index,
                                            position: 'relative'
                                        }}
                                        onMouseDown={(e) => e.target.style.cursor = 'grabbing'}
                                        onMouseUp={(e) => e.target.style.cursor = 'grab'}
                                    >
                                        {editingId === item.id ? (
                                            <input
                                                type="text"
                                                defaultValue={item.value}
                                                autoFocus
                                                onBlur={(e) => editItem(item.id, e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        editItem(item.id, e.target.value);
                                                    }
                                                }}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    outline: 'none',
                                                    textAlign: 'center',
                                                    fontWeight: '600',
                                                    fontSize: '1rem',
                                                    color: 'white',
                                                    width: '100%'
                                                }}
                                            />
                                        ) : (
                                            <>
                                                {item.value}
                                                {index === 0 && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '-10px',
                                                        right: '-10px',
                                                        background: 'linear-gradient(135deg, #ff6b6b, #ee5a52)',
                                                        color: 'white',
                                                        borderRadius: '50%',
                                                        width: '24px',
                                                        height: '24px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '0.7rem',
                                                        fontWeight: '700',
                                                        boxShadow: '0 2px 8px rgba(255, 107, 107, 0.4)',
                                                        border: '2px solid white'
                                                    }}>
                                                        TOP
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Operation Indicator */}
                        {animatingItem && (
                            <div style={{
                                position: 'absolute',
                                top: '10px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                padding: '8px 16px',
                                background: 'rgba(102, 126, 234, 0.9)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '20px',
                                color: 'white',
                                fontWeight: '600',
                                fontSize: '0.9rem',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                border: '1px solid rgba(255, 255, 255, 0.2)'
                            }}>
                                {animatingItem.operation === 'push' && '⬇️ PUSHING'}
                                {animatingItem.operation === 'pop' && '⬆️ POPPING'}
                                {animatingItem.operation === 'clear' && '🗑️ CLEARING'}
                                {animatingItem.operation === 'delete' && '❌ DELETING'}
                            </div>
                        )}
                    </div>
                </div>

                {/* Garbage Zone */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '20px'
                }}>
                    <div
                        ref={garbageRef}
                        onDragOver={(e) => handleDragOver(e, 'garbage')}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, 'garbage')}
                        style={{
                            width: '200px',
                            height: '80px',
                            background: dragOverZone === 'garbage'
                                ? 'linear-gradient(135deg, #ff6b6b, #ee5a52)'
                                : 'linear-gradient(135deg, #9ca3af, #6b7280)',
                            borderRadius: '16px',
                            border: dragOverZone === 'garbage'
                                ? '3px dashed #ffffff'
                                : '2px solid rgba(255, 255, 255, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '1rem',
                            transition: 'all 0.3s ease',
                            cursor: 'default',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                        }}
                    >
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1.5rem', marginBottom: '5px' }}>
                                {dragOverZone === 'garbage' ? '🔥' : '🗑️'}
                            </div>
                            <div style={{ fontSize: '0.85rem' }}>
                                {dragOverZone === 'garbage' ? 'Drop to Delete!' : 'Drag Here to Delete'}
                            </div>
                        </div>
                    </div>
                </div>


                <br></br>
                <br></br>

                {/* Language Selector */}
                <label style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: 'white',
                    marginBottom: '8px',
                    letterSpacing: '0.025em',
                    display: 'block',
                    color: darkMode ? "#fff" : "#0c0c0cff"
                }}>
                    {selLanguage ? "Γλώσσα Προγραμματισμού" : "Programming Language"}
                </label>

                {/* Language Selector */}
                <div style={{ marginBottom: '20px' }}>
                    <select
                        value={codeLanguage}
                        onChange={(e) => setCodeLanguage(e.target.value)}
                        style={{
                            padding: '12px 16px',
                            border: '2px solid #e5e7eb',
                            borderRadius: '12px',
                            fontSize: '0.95rem',
                            background: 'white',
                            transition: 'all 0.2s ease',
                            fontFamily: 'inherit',
                            cursor: 'pointer',
                            width: '200px'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    >
                        <option value="javascript">JavaScript</option>
                        <option value="python">Python</option>
                        <option value="cpp">C++</option>
                        <option value="java">Java</option>
                    </select>
                </div>
                {/* Code Display */}
                <div style={codeDisplayStyle}>
                    <CodeHighlighter code={generateCode()} language={codeLanguage} />
                </div>

                {/* Copy Button */}
                <button
                    onClick={() => {
                        navigator.clipboard.writeText(generateCode());
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                    }}
                    style={{
                        ...buttonStyle,
                        width: '100%',
                        marginTop: '20px',
                        background: copied
                            ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                            : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        transform: copied ? 'translateY(-4px)' : 'translateY(0)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => !copied && (e.target.style.transform = 'translateY(-2px)')}
                    onMouseOut={(e) => !copied && (e.target.style.transform = 'translateY(0)')}
                >
                    <Copy size={16} style={{ marginRight: '8px' }} />
                    {copied ? (selLanguage ? ' Αντιγράφηκε!' : ' Copied') : (selLanguage ? ' Αντιγραφή' : ' Copy')}
                </button>
            </div>
        </div>
    );
};

export default StackBuilder;